import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../hooks/useAuth';
import { Recipe } from '../types/mealPlan';
import {
  Stack,
  Title,
  Text,
  Paper,
  Group,
  Card,
  Badge,
  SimpleGrid,
  Alert,
  Select,
  Pagination,
  ActionIcon,
  Skeleton,
  Tabs,
  Anchor,
} from '@mantine/core';
import {
  IconBook,
  IconClock,
  IconUsers,
  IconAlertCircle,
  IconInfoCircle,
  IconLock,
  IconArrowUp,
  IconArrowDown,
  IconPalette,
  IconPencil,
} from '@tabler/icons-react';
import { formatTime } from '../utils/formatTime';
import { RecipeSearchFilters } from '../components/Recipe/RecipeSearchFilters';
import { RecipeImage } from '../components/UI/RecipeImage';
import { TasteProfileData, SlotTasteProfiles } from '../types/tasteProfile';
import { TasteProfileSection } from '../components/TasteProfile/TasteProfileSection';
import { TastePersonalityCard } from '../components/TasteProfile/TastePersonalityCard';
import { buildRecipePath } from '../utils/routeHelpers';
import { RecipeProvenanceBadges } from '../components/Recipe/RecipeProvenanceBadges';
import { getRecipeBadges, splitBadgesForDisplay } from '../utils/recipeBadges';
import { MoreRestrictionsMenu } from '../components/UI/MoreRestrictionsMenu';
import { CuisineBadge } from '../components/UI/CuisineBadge';
import { getPageCache, setPageCache } from '../utils/pageCache';
import { publicProfileCacheKey } from '../utils/publicProfileCache';
import { ProfileIdentityModal } from '../components/Profile/ProfileIdentityModal';
import { EditableAvatar } from '../components/Profile/EditableAvatar';
import { ProfileVisibilityBadge } from '../components/Profile/ProfileVisibilityBadge';
import { UserAvatar } from '../components/UI/UserAvatar';
import { SUMMARY_VIEW_PARAM } from '../utils/recipeSummary';
import { formatCount } from '../utils/formatCount';
import {
  ownerDisplayName,
  ownerMidSentence,
  ownerSentenceSubject,
} from '../utils/ownerName';

interface OwnerInfo {
  uid: string;
  displayName?: string;
  photoURL?: string;
}

type PublicProfileCacheEntry = {
  recipes: Recipe[];
  owner: OwnerInfo | null;
  isOwner: boolean;
  profilePublic: boolean;
  recipeCount: number;
  tasteProfile: TasteProfileData | null;
  slotTasteProfiles?: SlotTasteProfiles;
};

export const PublicProfilePage = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  // Seed from what this profile displayed earlier in the session (keyed by
  // viewer too — isOwner and private-recipe visibility depend on who's
  // looking), so a back navigation renders it on the first paint and the
  // fetch below refreshes silently.
  const profileCacheKey = publicProfileCacheKey(userId, user?.uid);
  const seededProfile = getPageCache<PublicProfileCacheEntry>(profileCacheKey);
  const [recipes, setRecipes] = useState<Recipe[]>(seededProfile?.recipes ?? []);
  const [owner, setOwner] = useState<OwnerInfo | null>(seededProfile?.owner ?? null);
  const [isOwner, setIsOwner] = useState(seededProfile?.isOwner ?? false);
  const [profilePublic, setProfilePublic] = useState(seededProfile?.profilePublic ?? false);
  const [recipeCount, setRecipeCount] = useState(seededProfile?.recipeCount ?? 0);
  const [tasteProfile, setTasteProfile] = useState<TasteProfileData | null>(seededProfile?.tasteProfile ?? null);
  const [slotTasteProfiles, setSlotTasteProfiles] = useState<SlotTasteProfiles | undefined>(seededProfile?.slotTasteProfiles);
  const [loading, setLoading] = useState(!seededProfile);
  const [error, setError] = useState<string | null>(null);
  const [editingIdentity, setEditingIdentity] = useState(false);

  // Keep the tab title current on client-side navigation; full-load head tags
  // (title/meta/OG) are injected server-side, so this page renders none itself.
  usePageTitle(owner ? `${ownerDisplayName(owner.displayName)} | Meals You Love` : '');

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('');
  const [selectedDietaryRestrictions, setSelectedDietaryRestrictions] = useState<string[]>([]);
  const [selectedPrepTime, setSelectedPrepTime] = useState<string>('');
  const [selectedTotalTime, setSelectedTotalTime] = useState<string>('');

  // Sorting state
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const recipesPerPage = 12;
  const firstRecipeRef = useRef<HTMLDivElement>(null);
  const hasInitiallyLoadedRef = useRef(false);

  // Suppress ResizeObserver errors - this is a common, harmless browser issue
  // Reference: https://trackjs.com/javascript-errors/resizeobserver-loop-completed-with-undelivered-notifications/
  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    };

    window.addEventListener('error', handleError);

    return () => window.removeEventListener('error', handleError);
  }, []);

  useEffect(() => {
    // Wait for auth to finish loading before fetching
    if (authLoading) {
      return;
    }

    if (!userId) {
      setError('User not found');
      setLoading(false);
      return;
    }

    const cacheKey = publicProfileCacheKey(userId, user?.uid);

    const fetchCookbook = async () => {
      try {
        // With seeded content this is a silent background refresh.
        if (!getPageCache(cacheKey)) {
          setLoading(true);
        }
        setError(null);

        // Use public endpoint with optional auth
        const headers: HeadersInit = {};
        if (user) {
          const token = await user.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/u/${userId}?${SUMMARY_VIEW_PARAM}`, {
          headers,
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('This profile is private');
          }
          throw new Error('Failed to load cookbook');
        }

        const data = await response.json();
        setRecipes(data.recipes || []);
        setOwner(data.owner);
        setIsOwner(data.isOwner ?? false);
        setProfilePublic(data.profilePublic ?? false);
        setRecipeCount(data.recipeCount ?? 0);
        setTasteProfile(data.tasteProfile ?? null);
        setSlotTasteProfiles(data.slotTasteProfiles);
        setPageCache(cacheKey, {
          recipes: data.recipes || [],
          owner: data.owner,
          isOwner: data.isOwner ?? false,
          profilePublic: data.profilePublic ?? false,
          recipeCount: data.recipeCount ?? 0,
          tasteProfile: data.tasteProfile ?? null,
          slotTasteProfiles: data.slotTasteProfiles,
        });
      } catch (error: any) {
        console.error('Error fetching cookbook:', error);
        // Keep showing seeded content on a background-refresh failure.
        if (!getPageCache(cacheKey)) {
          setError(error.message || 'Failed to load cookbook');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCookbook();
  }, [userId, user, authLoading]);

  // The owner just edited their name or photo: mirror it into state and the
  // cache so this page shows it now and after a back navigation, instead of
  // waiting for the next fetch of the cookbook.
  const handleIdentitySaved = (identity: { displayName?: string; photoURL?: string }) => {
    if (!userId) return;
    const updatedOwner: OwnerInfo = {
      uid: owner?.uid ?? userId,
      displayName: identity.displayName,
      photoURL: identity.photoURL,
    };
    setOwner(updatedOwner);
    const cached = getPageCache<PublicProfileCacheEntry>(profileCacheKey);
    if (cached) {
      setPageCache(profileCacheKey, { ...cached, owner: updatedOwner });
    }
  };

  // Same mirror for the public/private switch in the header.
  const handleVisibilityChange = (nextProfilePublic: boolean) => {
    setProfilePublic(nextProfilePublic);
    const cached = getPageCache<PublicProfileCacheEntry>(profileCacheKey);
    if (cached) {
      setPageCache(profileCacheKey, { ...cached, profilePublic: nextProfilePublic });
    }
  };

  // Generate cuisine and dietary options from recipes
  const cuisineOptions = useMemo(() => {
    const cuisines = new Set(recipes.map(r => r.cuisine).filter(Boolean) as string[]);
    return Array.from(cuisines).sort();
  }, [recipes]);

  const dietaryOptions = useMemo(() => {
    const restrictions = new Set<string>();
    recipes.forEach(recipe => {
      recipe.dietaryRestrictions?.forEach(restriction => restrictions.add(restriction));
    });
    return Array.from(restrictions).sort();
  }, [recipes]);

  // Filter and sort recipes
  const filteredRecipes = useMemo(() => {
    let filtered = [...recipes];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(recipe =>
        recipe.name.toLowerCase().includes(query) ||
        recipe.description?.toLowerCase().includes(query) ||
        recipe.cuisine?.toLowerCase().includes(query)
      );
    }

    // Cuisine filter
    if (selectedCuisines.length > 0) {
      filtered = filtered.filter(recipe =>
        recipe.cuisine && selectedCuisines.includes(recipe.cuisine)
      );
    }

    // Difficulty filter
    if (selectedDifficulty) {
      filtered = filtered.filter(recipe => recipe.difficulty === selectedDifficulty);
    }

    // Prep time filter
    if (selectedPrepTime) {
      filtered = filtered.filter(recipe => {
        const prepTime = recipe.prepTimeMinutes;
        switch (selectedPrepTime) {
          case 'quick': return prepTime <= 15;
          case 'medium': return prepTime > 15 && prepTime <= 30;
          case 'long': return prepTime > 30;
          default: return true;
        }
      });
    }

    // Total time filter
    if (selectedTotalTime) {
      filtered = filtered.filter(recipe => {
        const totalTime = recipe.totalTimeMinutes;
        switch (selectedTotalTime) {
          case 'quick': return totalTime <= 30;
          case 'medium': return totalTime > 30 && totalTime <= 60;
          case 'long': return totalTime > 60;
          default: return true;
        }
      });
    }

    // Dietary restrictions filter
    if (selectedDietaryRestrictions.length > 0) {
      filtered = filtered.filter(recipe => {
        const recipeRestrictions = recipe.dietaryRestrictions || [];
        return selectedDietaryRestrictions.every(restriction =>
          recipeRestrictions.includes(restriction)
        );
      });
    }

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'created':
          aValue = new Date(a.createdAt || 0);
          bValue = new Date(b.createdAt || 0);
          break;
        case 'totalTime':
          aValue = a.totalTimeMinutes;
          bValue = b.totalTimeMinutes;
          break;
        case 'difficulty':
          const difficultyOrder = { 'easy': 1, 'medium': 2, 'hard': 3 };
          aValue = difficultyOrder[a.difficulty || 'medium'];
          bValue = difficultyOrder[b.difficulty || 'medium'];
          break;
        case 'cuisine':
          aValue = (a.cuisine || '').toLowerCase();
          bValue = (b.cuisine || '').toLowerCase();
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [recipes, searchQuery, selectedCuisines, selectedDifficulty, selectedPrepTime, selectedTotalTime, selectedDietaryRestrictions, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredRecipes.length / recipesPerPage);
  const startIndex = (currentPage - 1) * recipesPerPage;
  const endIndex = startIndex + recipesPerPage;
  const currentRecipes = filteredRecipes.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCuisines, selectedDifficulty, selectedPrepTime, selectedTotalTime, selectedDietaryRestrictions]);

  // Scroll to top when page changes (but not on initial load)
  useEffect(() => {
    if (firstRecipeRef.current && currentRecipes.length > 0 && hasInitiallyLoadedRef.current) {
      // Use requestAnimationFrame for better timing with DOM updates
      requestAnimationFrame(() => {
        firstRecipeRef.current?.scrollIntoView({
          behavior: 'auto',
          block: 'center' // Use center to avoid being cut off by the navbar
        });
      });
    }
    // Mark as initially loaded after first render
    if (currentRecipes.length > 0 && !hasInitiallyLoadedRef.current) {
      hasInitiallyLoadedRef.current = true;
    }
  }, [currentPage, currentRecipes.length]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCuisines([]);
    setSelectedDifficulty('');
    setSelectedDietaryRestrictions([]);
    setSelectedPrepTime('');
    setSelectedTotalTime('');
  };

  const handleRecipeClick = (recipe: Recipe) => {
    navigate(buildRecipePath(recipe, userId!), {
      state: { recipe }
    });
  };

  if (loading) {
    return (
      <Stack gap="lg">
        {/* Profile header skeleton */}
        <Paper p="lg" radius="lg" withBorder>
          <Group justify="space-between" wrap="wrap" align="center">
            <Group>
              <Skeleton height={64} width={64} circle style={{ flexShrink: 0 }} />
              <Stack gap="sm">
                <Group gap="sm" align="center">
                  <Skeleton height={28} width={200} />
                  <Skeleton height={20} width={70} radius="xl" visibleFrom="md" />
                </Group>
                <Skeleton height={14} width={120} />
                <Skeleton height={20} width={70} radius="xl" hiddenFrom="md" />
              </Stack>
            </Group>
            <Stack gap="xs" style={{ flex: '1 1 300px', maxWidth: 550, padding: '13px 0' }}>
              <Skeleton height={16} width={140} />
              <Skeleton height={12} width="90%" />
              <Skeleton height={12} width="70%" />
              <Skeleton height={12} width="50%" />
            </Stack>
          </Group>
        </Paper>

        {/* Tabs skeleton */}
        <Group gap="lg">
          <Skeleton height={36} width={110} radius="sm" />
          <Skeleton height={36} width={120} radius="sm" />
        </Group>

        {/* Search/filter skeleton */}
        <Paper p="md" radius="lg" withBorder>
          <Group gap="md">
            <Skeleton height={36} radius="sm" style={{ flex: 1 }} />
            <Skeleton height={36} width={150} radius="xl" />
          </Group>
        </Paper>

        {/* Sort/count skeleton */}
        <Group justify="space-between" align="center">
          <Skeleton height={20} width="min(300px, 85%)" />
          <Group gap="sm">
            <Skeleton height={36} width={150} radius="sm" />
            <Skeleton height={28} width={50} radius="md" />
          </Group>
        </Group>

        {/* Recipe grid skeleton */}
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} padding="lg" radius="lg" withBorder>
              <Card.Section style={{ position: 'relative' }}>
                <Skeleton height={200} radius={0} />
                <Skeleton height={20} width={70} radius="xl" style={{ position: 'absolute', top: 8, left: 8 }} />
              </Card.Section>
              <Stack gap="xs" mt="md" style={{ flex: 1, justifyContent: 'space-between' }}>
                <Stack gap="xs">
                  <Skeleton height={18} width="80%" />
                  <Skeleton height={14} width="95%" />
                  <Skeleton height={14} width="60%" />
                </Stack>
                <Stack gap="xs">
                  {/* Time + servings + difficulty + source */}
                  <Group gap="md">
                    <Group gap="xs">
                      <Skeleton height={16} width={16} radius="sm" />
                      <Skeleton height={14} width={50} />
                    </Group>
                    <Group gap="xs">
                      <Skeleton height={16} width={16} radius="sm" />
                      <Skeleton height={14} width={20} />
                    </Group>
                    <Skeleton height={20} width={50} radius="xl" />
                    <Skeleton height={20} width={95} radius="xl" />
                  </Group>
                  {/* Dietary/tag badges */}
                  <Group gap="xs">
                    <Skeleton height={20} width={70} radius="xl" />
                    <Skeleton height={20} width={55} radius="xl" />
                    <Skeleton height={20} width={65} radius="xl" />
                  </Group>
                </Stack>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>
    );
  }

  if (error) {
    return (
      <Paper p="xl" radius="lg" withBorder>
        <Stack gap="md" align="center">
          {error === 'This profile is private' ? (
            <>
              <IconLock size={48} color="var(--mantine-color-gray-5)" />
              <Text fw={500}>This Profile is Private</Text>
              <Text c="dimmed" size="sm" ta="center">
                This user has chosen to keep their cookbook private.
              </Text>
            </>
          ) : (
            <>
              <IconAlertCircle size={48} color="var(--mantine-color-red-5)" />
              <Text fw={500}>Cookbook Not Found</Text>
              <Text c="dimmed" size="sm" ta="center">
                {error}
              </Text>
            </>
          )}
        </Stack>
      </Paper>
    );
  }

  // Head tags (title/description/OG) are injected server-side on full page
  // loads (meals-you-love og.ts); the tab title is kept current via
  // usePageTitle above, so this page renders no head tags of its own.
  return (
    <Stack gap="lg">
      {/* Profile Header */}
      <Paper p="lg" radius="lg" withBorder>
        <Group justify="space-between" wrap="wrap" align="center">
          <Group>
            {isOwner ? (
              <EditableAvatar
                photoURL={owner?.photoURL}
                displayName={owner?.displayName}
                size={64}
                onClick={() => setEditingIdentity(true)}
                style={tasteProfile?.personalityName ? { border: '2px solid var(--mantine-color-violet-4)' } : undefined}
              />
            ) : (
              <UserAvatar
                photoURL={owner?.photoURL}
                displayName={owner?.displayName}
                size={64}
                style={tasteProfile?.personalityName ? { border: '2px solid var(--mantine-color-violet-4)' } : undefined}
              />
            )}
            <Stack gap="xs">
              <Group gap="sm" align="center">
                <Title order={2}>{ownerDisplayName(owner?.displayName)}</Title>
                {isOwner && (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => setEditingIdentity(true)}
                    aria-label="Edit your name and photo"
                  >
                    <IconPencil size={18} />
                  </ActionIcon>
                )}
                {isOwner && (
                  <ProfileVisibilityBadge
                    profilePublic={profilePublic}
                    onChange={handleVisibilityChange}
                    visibleFrom="md"
                  />
                )}
              </Group>
              <Text c="dimmed" size="sm">
                {formatCount(recipeCount)} {recipeCount === 1 ? 'recipe' : 'recipes'} shared
              </Text>
              {isOwner && (!owner?.displayName || !owner?.photoURL) && (
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  onClick={() => setEditingIdentity(true)}
                  style={{ alignSelf: 'flex-start' }}
                >
                  {!owner?.displayName && !owner?.photoURL
                    ? 'Add your name and photo'
                    : !owner?.displayName
                      ? 'Add your name'
                      : 'Add a photo'}
                </Anchor>
              )}
              {isOwner && (
                <ProfileVisibilityBadge
                  profilePublic={profilePublic}
                  onChange={handleVisibilityChange}
                  hiddenFrom="md"
                  style={{ alignSelf: 'flex-start' }}
                />
              )}
            </Stack>
          </Group>
          {tasteProfile && (
            <TastePersonalityCard
              name={tasteProfile.personalityName}
              blurb={tasteProfile.personalityBlurb}
              ownerName={ownerSentenceSubject(owner?.displayName)}
            />
          )}
        </Group>
      </Paper>

      {isOwner && (
        <ProfileIdentityModal
          opened={editingIdentity}
          onClose={() => setEditingIdentity(false)}
          displayName={owner?.displayName}
          photoURL={owner?.photoURL}
          onSaved={handleIdentitySaved}
        />
      )}

      <Tabs defaultValue="cookbook">
        <Tabs.List>
          <Tabs.Tab value="cookbook" leftSection={<IconBook size={16} />}>
            Cookbook
          </Tabs.Tab>
          <Tabs.Tab value="taste" leftSection={<IconPalette size={16} />}>
            Taste profile
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="cookbook" pt="lg">
          <Stack gap="lg">
            {/* Search and Filters */}
            {recipes.length > 0 && (
              <Paper p="md" radius="lg" withBorder>
                <RecipeSearchFilters
                  searchQuery={searchQuery}
                  onSearchQueryChange={setSearchQuery}
                  selectedCuisines={selectedCuisines}
                  selectedDifficulty={selectedDifficulty}
                  selectedDietaryRestrictions={selectedDietaryRestrictions}
                  selectedPrepTime={selectedPrepTime}
                  selectedTotalTime={selectedTotalTime}
                  cuisineOptions={cuisineOptions}
                  dietaryOptions={dietaryOptions}
                  onSelectedCuisinesChange={setSelectedCuisines}
                  onSelectedDifficultyChange={setSelectedDifficulty}
                  onSelectedDietaryRestrictionsChange={setSelectedDietaryRestrictions}
                  onSelectedPrepTimeChange={setSelectedPrepTime}
                  onSelectedTotalTimeChange={setSelectedTotalTime}
                  onClearFilters={clearFilters}
                  showCollapsible={true}
                  showNeedsReviewFilter={false}
                  showClearAllButton={true}
                  defaultExpanded={false}
                  searchPlaceholder="Search recipes by name, description, or cuisine..."
                />
              </Paper>
            )}

            {/* Sort Options and Results Count */}
            {recipes.length > 0 && (
              <Group justify="space-between" align="center">
                <Text fw={500}>
                  {formatCount(filteredRecipes.length)} recipe{filteredRecipes.length !== 1 ? 's' : ''} found
                  {filteredRecipes.length > recipesPerPage && (
                    <Text component="span" c="dimmed" size="sm">
                      {' '}• Showing {formatCount(startIndex + 1)}-{formatCount(Math.min(endIndex, filteredRecipes.length))} of {formatCount(filteredRecipes.length)}
                    </Text>
                  )}
                </Text>
                <Group gap="xs">
                  <Select
                    placeholder="Sort by"
                    value={sortField}
                    onChange={(value) => setSortField(value || 'name')}
                    data={[
                      { value: 'name', label: 'Name' },
                      { value: 'created', label: 'Date added' },
                      { value: 'totalTime', label: 'Total time' },
                      { value: 'difficulty', label: 'Difficulty' },
                      { value: 'cuisine', label: 'Cuisine' },
                    ]}
                    w={150}
                  />
                  <Group gap={4}>
                    <Text size="sm" c="dimmed">
                      {sortDirection === 'asc' ? 'A-Z' : 'Z-A'}
                    </Text>
                    <ActionIcon
                      variant="subtle"
                      onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                    >
                      {sortDirection === 'asc' ? <IconArrowUp size={18} /> : <IconArrowDown size={18} />}
                    </ActionIcon>
                  </Group>
                </Group>
              </Group>
            )}

            {/* No recipes message */}
            {recipes.length === 0 && (
              <Alert icon={<IconBook size={16} />} color="blue" radius="lg">
                <Text size="sm">
                  {isOwner
                    ? "You haven't saved any recipes to your cookbook yet."
                    : `${ownerSentenceSubject(owner?.displayName)} hasn't shared any recipes yet.`
                  }
                </Text>
              </Alert>
            )}

            {/* No filtered results message */}
            {recipes.length > 0 && filteredRecipes.length === 0 && (
              <Alert icon={<IconAlertCircle size={16} />} color="blue" radius="lg">
                <Text size="sm">
                  No recipes match your search criteria. Try adjusting your filters.
                </Text>
              </Alert>
            )}

            {/* Recipe Grid */}
            {currentRecipes.length > 0 && (
              <SimpleGrid
                cols={{ base: 1, sm: 2, md: 3 }}
                spacing="md"
              >
                {currentRecipes.map((recipe, index) => (
                  <Card
                    key={recipe.id}
                    ref={index === 0 ? firstRecipeRef : undefined}
                    padding="lg"
                    radius="lg"
                    withBorder
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleRecipeClick(recipe)}
                  >
                    <Card.Section style={{ position: 'relative' }}>
                      <RecipeImage
                        src={recipe.imageUrl}
                        alt={recipe.name}
                        height={200}
                      />
                      {recipe.cuisine && (
                        <CuisineBadge
                          cuisine={recipe.cuisine}
                          placement="overlay"
                          style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                          }}
                        />
                      )}
                    </Card.Section>

                    <Stack gap="xs" mt="md" style={{ flex: 1, justifyContent: 'space-between' }}>
                      <Stack gap="xs">
                        <Text fw={500} lineClamp={2}>
                          {recipe.name}
                        </Text>

                        {recipe.description && (
                          <Text size="sm" c="dimmed" lineClamp={2}>
                            {recipe.description}
                          </Text>
                        )}
                      </Stack>

                      <Stack gap="xs">
                        {/* Desktop: Time, servings, and functional badges on one line */}
                        <Group gap="md" visibleFrom="lg">
                          <Group gap="xs">
                            <IconClock size={16} />
                            <Text size="sm">{formatTime(recipe.totalTimeMinutes)}</Text>
                          </Group>
                          <Group gap="xs">
                            <IconUsers size={16} />
                            <Text size="sm">{recipe.servings}</Text>
                          </Group>
                          <Group gap="xs">
                            {recipe.difficulty && (
                              <Badge
                                size="sm"
                                variant="light"
                                color={recipe.difficulty === 'easy' ? 'green' : recipe.difficulty === 'medium' ? 'yellow' : 'red'}
                              >
                                {recipe.difficulty}
                              </Badge>
                            )}
                            <RecipeProvenanceBadges recipe={recipe} />
                          </Group>
                        </Group>

                        {/* Mobile/Tablet: Time and servings on one line */}
                        <Group gap="md" hiddenFrom="lg">
                          <Group gap="xs">
                            <IconClock size={16} />
                            <Text size="sm">{formatTime(recipe.totalTimeMinutes)}</Text>
                          </Group>
                          <Group gap="xs">
                            <IconUsers size={16} />
                            <Text size="sm">{recipe.servings}</Text>
                          </Group>
                        </Group>

                        {/* Mobile/Tablet: Functional badges on separate line */}
                        <Group gap="xs" hiddenFrom="lg">
                          {recipe.difficulty && (
                            <Badge
                              size="sm"
                              variant="light"
                              color={recipe.difficulty === 'easy' ? 'green' : recipe.difficulty === 'medium' ? 'yellow' : 'red'}
                            >
                              {recipe.difficulty}
                            </Badge>
                          )}
                          <RecipeProvenanceBadges recipe={recipe} />
                        </Group>

                        {/* Tags on second/third line */}
                        <Group gap="xs">
                          {(() => {
                            // Anonymous viewer: no profile to personalize by, so the
                            // helper's defaults apply (family-only tags stay visible)
                            const allBadges = getRecipeBadges(recipe);
                            const { visible, hiddenRestrictions } = splitBadgesForDisplay(allBadges, 3);

                            return (
                              <>
                                {visible.map((badge) => (
                                  <Badge key={`${badge.type}-${badge.text}`} size="sm" variant="outline">
                                    {badge.text}
                                  </Badge>
                                ))}
                                <MoreRestrictionsMenu restrictions={hiddenRestrictions} />
                              </>
                            );
                          })()}
                        </Group>
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Group justify="center" mt="xl">
                <Pagination
                  total={totalPages}
                  value={currentPage}
                  onChange={setCurrentPage}
                  siblings={0}
                />
              </Group>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="taste" pt="lg">
          {tasteProfile ? (
            <TasteProfileSection
              tasteProfile={tasteProfile}
              slotTasteProfiles={slotTasteProfiles}
              ownerName={ownerMidSentence(owner?.displayName)}
            />
          ) : (
            <Alert icon={<IconInfoCircle size={16} />} color="gray" radius="lg">
              <Text size="sm">
                {isOwner
                  ? "Your taste profile will appear here as you use Meals You Love. Keep cooking!"
                  : `${ownerSentenceSubject(owner?.displayName)}'s taste profile will appear once there's more data.`
                }
              </Text>
            </Alert>
          )}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};
